import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { initOnboarding, bookmarkStep } from '@/api/onboarding'
import { track, trackError } from '@/lib/analytics'
import { getPollsFlow, setPollsFlow } from '@/onboarding/pollsFlow'

const BOT_USERNAME = '@id510107251374_bot'
const BOT_DISPLAY_NAME = 'TeleGraphyx бот продвижения'

export function SourcePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ref = params.get('ref')
    if (ref) setPollsFlow(ref)
    else if (getPollsFlow().active) setPollsFlow(getPollsFlow().ref)
  }, [params])

  async function handleConnect() {
    setSubmitting(true)
    setError(null)
    try {
      const res = await initOnboarding('channel')
      // Намеренный click — фиксируем перед редиректом на connect-subscriber.
      // Сравнение с channel_connected даст конверсию «нажал подключить канал»
      // → «канал реально подключился к боту».
      track('channel_connect_started', { message: 'клик «Подключить канал»' })
      bookmarkStep('connect-subscriber')
      navigate(`/onboarding/connect-subscriber/${encodeURIComponent(res.onboardToken)}`, {
        state: { maxDeepLink: res.maxDeepLink, expiresAt: res.expiresAt },
      })
    } catch (e) {
      trackError(e, 'init-onboarding')
      setError(e instanceof Error ? e.message : 'Не удалось начать подключение')
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      step="source"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/overview')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleConnect} disabled={submitting}>
            {submitting ? 'Создаём сессию…' : 'Подключить'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill">Подключение</span>
        Источник в MAX
      </p>
      <h1 className="stage-title">
        Подключите канал или&nbsp;группу <em>в&nbsp;MAX</em>.
      </h1>
      <p className="stage-sub">
        Подключение работает одинаково для каналов и групп: добавьте бот <code className="bot-code">{BOT_USERNAME}</code> администратором&nbsp;— и&nbsp;TeleGraphyx начнёт собирать аналитику. <b>Боты как самостоятельный источник — скоро.</b>
      </p>

      <div className="src-grid">
        <div className="src-card channel selected">
          <div className="src-card-icons">
            <div className="src-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11l18-8-3 18-7-5-3 3v-5l8-7-9 5-4-1z" />
              </svg>
            </div>
            <div className="src-icon src-icon--2nd">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
          </div>
          <div className="src-name">Канал или группа MAX</div>
          <div className="src-desc">
            Аналитика охватов и подписчиков, реклама в&nbsp;Я.Директе, умные кнопки, лендинги, модерация комментариев.
          </div>
          <div className="src-tags">
            <span className="src-tag recommend">доступно</span>
            <span className="src-tag">аналитика</span>
            <span className="src-tag">реклама</span>
            <span className="src-tag">кнопки</span>
          </div>
        </div>

        <div className="src-card bot disabled">
          <div className="src-card-icons">
            <div className="src-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="6" width="16" height="14" rx="3" />
                <circle cx="9" cy="13" r="1.5" />
                <circle cx="15" cy="13" r="1.5" />
                <path d="M12 2v4M9 17h6" />
              </svg>
            </div>
          </div>
          <div className="src-name">
            Бот MAX <span className="src-name-badge">скоро</span>
          </div>
          <div className="src-desc">
            Автоматизация диалогов и воронок, рассылки и интеграции с&nbsp;CRM. Добавим в&nbsp;ближайших релизах.
          </div>
          <div className="src-tags">
            <span className="src-tag">диалоги</span>
            <span className="src-tag">воронки</span>
          </div>
        </div>
      </div>

      {error && <div className="onb-error">{error}</div>}

      <div className="info-strip">
        <div className="info-strip-cell">
          <p className="info-strip-eye">Что понадобится</p>
          <p className="info-strip-text">
            Бот <b>«{BOT_DISPLAY_NAME}»</b> в&nbsp;администраторах канала или&nbsp;группы.
          </p>
        </div>
        <div className="info-strip-cell">
          <p className="info-strip-eye">Сколько занимает</p>
          <p className="info-strip-text">
            Около <b>2 минут</b>. Автопроверка без перезагрузки.
          </p>
        </div>
        <div className="info-strip-cell">
          <p className="info-strip-eye">Безопасность</p>
          <p className="info-strip-text">
            Никаких банов, удалений и модерации <em>с нашей стороны</em>.
          </p>
        </div>
      </div>

      <style>{`
        .bot-code {
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 13px;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-blue-press, #0066cc);
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 600;
          overflow-wrap: anywhere;
        }
        .src-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
          gap: 20px;
          max-width: 900px;
          margin-bottom: 24px;
          width: 100%;
          min-width: 0;
        }
        .src-card {
          background: var(--tg-surface, #fff);
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 28px 24px 24px;
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 14px;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .src-card.selected {
          border-color: var(--tg-blue, #0080ff);
          background:
            radial-gradient(circle at 100% 0%, var(--tg-blue-bg, #ebf2ff) 0%, transparent 60%),
            var(--tg-surface, #fff);
          box-shadow: 0 8px 24px -8px rgba(0,128,255,0.3);
        }
        .src-card.selected::after {
          content: "";
          position: absolute;
          top: 16px; right: 16px;
          width: 22px; height: 22px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6L9 17l-5-5'/%3E%3C/svg%3E");
          background-size: 14px;
          background-position: center;
          background-repeat: no-repeat;
        }
        .src-card.disabled { opacity: 0.55; }
        .src-card-icons {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .src-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .src-card.channel .src-icon {
          background: linear-gradient(135deg, rgba(0,128,255,0.14), rgba(0,128,255,0.06));
          color: var(--tg-blue-press, #0066cc);
        }
        .src-card.channel .src-icon--2nd {
          background: linear-gradient(135deg, rgba(120,80,220,0.14), rgba(120,80,220,0.06));
          color: rgb(90, 50, 180);
        }
        .src-card.bot .src-icon {
          background: linear-gradient(135deg, rgba(255,140,66,0.16), rgba(255,140,66,0.06));
          color: rgb(220, 100, 30);
        }
        .src-icon svg { width: 28px; height: 28px; }
        .src-name {
          font-weight: 800;
          font-size: 19px;
          letter-spacing: -0.015em;
          color: var(--tg-ink, #111);
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-wrap: anywhere;
        }
        .src-name-badge {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.06em;
          padding: 3px 8px;
          background: var(--tg-mute, #8f9eb0);
          color: white;
          border-radius: 4px;
          text-transform: uppercase;
        }
        .src-desc {
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
          flex: 1;
          overflow-wrap: anywhere;
        }
        .src-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          padding-top: 10px;
          border-top: 1px dashed var(--tg-line-2, #f0f0f4);
        }
        .src-tag {
          font-size: 10px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 4px;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink-2, #333);
          letter-spacing: 0.04em;
        }
        .src-tag.recommend {
          background: var(--tg-up, #00a860);
          color: white;
        }
        .onb-error {
          padding: 12px 16px;
          margin-bottom: 16px;
          background: rgba(255, 64, 0, 0.08);
          border: 1px solid rgba(255, 64, 0, 0.3);
          border-radius: 10px;
          color: #c33;
          font-size: 13px;
        }
        .info-strip {
          max-width: 900px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          padding: 0;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .info-strip-cell {
          padding: 18px 22px;
          border-right: 1px solid var(--tg-line-2, #f0f0f4);
          min-width: 0;
        }
        .info-strip-cell:last-child { border-right: 0; }
        .info-strip-eye {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          margin: 0 0 6px;
        }
        .info-strip-text {
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
          margin: 0;
        }
        .info-strip-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .info-strip-text em {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          color: var(--tg-mute, #8f9eb0);
        }

        @media (max-width: 720px) {
          .src-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .src-card.bot { display: none; }
          .src-card {
            padding: 22px 18px 18px;
          }
          .src-name { font-size: 17px; }
          .src-desc { font-size: 13px; }
          .info-strip {
            grid-template-columns: 1fr;
          }
          .info-strip-cell {
            border-right: 0;
            border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
            padding: 14px 18px;
          }
          .info-strip-cell:last-child { border-bottom: 0; }
        }
      `}</style>
    </OnboardingShell>
  )
}
